import { useEffect, useRef, useCallback } from 'react';
import io from 'socket.io-client';
import { getCurrentAccessToken } from '../services/api';

const useWebSocket = (apiUrl, objIdEmpresa, userID, onNovoPedido) => {
    const socketRef = useRef(null);
    const reconnectTimeoutRef = useRef(null);

    // ✅ Usar ref para callback evitar reconexões desnecessárias
    const onNovoPedidoRef = useRef(onNovoPedido);

    // Atualizar ref quando callback mudar (sem causar re-render)
    useEffect(() => {
        onNovoPedidoRef.current = onNovoPedido;
    }, [onNovoPedido]);

    const connect = useCallback(() => {
        // ✅ Verificar se já está conectado OU conectando
        if (socketRef.current?.connected || socketRef.current?.connecting) {
            console.log('[WS] Já conectado ou conectando, ignorando...');
            return;
        }

        console.log('[WS] Iniciando conexão WebSocket...');

        const socket = io(apiUrl, {
            withCredentials: true,
            transports: ['websocket'],
            auth: { token: getCurrentAccessToken() },
            reconnection: true,
            reconnectionAttempts: 10,
            reconnectionDelay: 5000,
        });

        socketRef.current = socket;

        socket.on("connect", () => {
            console.log("[WS] ✅ WebSocket conectado!");
            const clientId = `${userID}-${Date.now()}`;
            socket.emit('joinCompanyRoom', {
                companyId: objIdEmpresa.toString(),
                clientId
            });
        });

        socket.on("disconnect", (reason) => {
            console.log("[WS] ⚠️ WebSocket desconectado:", reason);
            // ✅ Não reconectar se foi desconexão intencional do cliente
            if (reason === 'io client disconnect') {
                console.log('[WS] Desconexão intencional, não reconectando.');
            }
        });

        socket.on("reconnect_attempt", (attempt) => {
            console.log(`[WS] 🔄 Tentativa de reconexão ${attempt}`);
        });

        socket.on("reconnect", () => {
            console.log("[WS] ✅ WebSocket reconectado!");
            const clientId = `${userID}-${Date.now()}`;
            socket.emit('joinCompanyRoom', {
                companyId: objIdEmpresa.toString(),
                clientId
            });
        });

        socket.once("ping", () => {
            console.log("[WS] 🔄 Ping recebido do servidor");
            socket.emit("pong");
        });

        socket.on('novoPedido', (data) => {
            console.log("[WS] 🎉 NOVO PEDIDO RECEBIDO!", data);
            // ✅ Usar ref para chamar callback atualizado
            if (onNovoPedidoRef.current) {
                onNovoPedidoRef.current(data);
            }
        });

        return socket;
    }, [apiUrl, objIdEmpresa, userID]); // ✅ Removido onNovoPedido das dependências

    const disconnect = useCallback(() => {
        if (reconnectTimeoutRef.current) {
            clearTimeout(reconnectTimeoutRef.current);
        }

        if (socketRef.current) {
            console.log('[WS] 🔌 Desconectando WebSocket...');
            socketRef.current.off('novoPedido');
            socketRef.current.disconnect();
            socketRef.current = null;
        }
    }, []);

    // ✅ Conectar apenas quando parâmetros essenciais mudarem
    useEffect(() => {
        if (!apiUrl || !objIdEmpresa || !userID) {
            console.log('[WS] Parâmetros incompletos, não conectando');
            return;
        }

        connect();

        return () => {
            disconnect();
        };
    }, [apiUrl, objIdEmpresa, userID]); // ✅ Dependências estáveis apenas

    return {
        socket: socketRef.current,
        connect,
        disconnect
    };
};

export default useWebSocket;
